/* tslint:disable:no-trailing-whitespace */
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import * as _ from 'lodash';
import { getPlatformByKey } from '../../../shared/services/constants/platform_types';
import { AnalyticsTrackingProvider } from '@yotpo-ui/core';
import { PlatformAnalyticsDataService } from '@yotpo-ui/platform-core';
import { first } from 'rxjs/operators';

export interface SignupEventData {
  stepNum?: number;
  stepName?: string;
  phase?: number;
  phaseName?: string;
  status?: string;
  url?: string;
  referrer?: string;
  product?: string;
  productIntent?: string;
  platform?: string;
  signupUTMCampaign?: string;
  signupUTMSource?: string;
  signupUTMMedium?: string;
  appKey?: string;
  website?: string;
  storeDomain?: string;
  email?: string;
  ctaText?: string;
}

@Injectable()
export class FlowTrackingService {
  private currentData: any;

  constructor(private redCrossService: AnalyticsTrackingProvider,
              private platformAnalyticsDataService: PlatformAnalyticsDataService) { }

  // Public Methods

  StepOneStarted(activatedRoute: ActivatedRouteSnapshot) {
    this.currentData = this.extractEventDataFromRoute(activatedRoute);

    let data = this.buildInitialEventData();
    data = this.applyStepData(data, 1, null, 'started');
    this.redCrossService.track('Signup Step', data, false);
  }

  StepOneCompleted() {
    let data = this.buildInitialEventData();
    data = this.applyStepData(data, 1, null, 'completed');
    this.platformAnalyticsDataService.clearCache();
    this.redCrossService.track('Signup Step', data, true);
  }

  StepTwoStarted() {
    let data = this.buildInitialEventData();
    data = this.applyStepData(data, 2, null, 'started');
    this.platformAnalyticsDataService.clearCache();
    this.redCrossService.track('Signup Step', data, true);
  }

  async StepTwoCompleted() {
    let data = this.buildInitialEventData();
    data = this.applyStepData(data, 2, null, 'completed');
    this.platformAnalyticsDataService.clearCache();
    await this.redCrossService.trackAsync('Signup Step', data, true);
  }

  StepTwoIntegrationCompleted() {
    let data = this.buildInitialEventData();
    data = this.applyStepData(data, 2, null, 'integration completed');
    this.platformAnalyticsDataService.clearCache();
    this.redCrossService.track('Signup Step', data, false);
    this.redCrossService.track('Signup', data, false);
  }

  StepAppStarted(activatedRoute: ActivatedRouteSnapshot) {
    this.currentData = this.extractEventDataFromRoute(activatedRoute);

    if (!this.currentData.platform) { // should not be feasible
      console.log('platform could not be resolved; analytics event aborted.');
      return;
    }

    // The utm params are set here according to the platform, so any utm param from the url is ignored
    this.currentData.signupUTMCampaign = `${this.currentData.platform}-app-signup`;
    this.currentData.signupUTMSource = 'app';
    this.currentData.product = this.currentData.product || 'Reviews';
    this.currentData.productIntent = this.currentData.productIntent || 'Reviews';

    let data = this.buildInitialEventData();
    data = this.applyStepData(data, 1, 'Signup App', 'started');
    this.redCrossService.track('Signup Step', data, false);
    this.redCrossService.track('Signup', data, false);
  }

  StepAppInProgress() {
    let data = this.buildInitialEventData();
    data = this.applyStepData(data, 1, 'Signup App', 'in-progress');
    this.redCrossService.track('Signup Step', data, false);
    this.redCrossService.track('Signup', data, false);
  }

  MobilePageStarted(activatedRoute: ActivatedRouteSnapshot) {
    this.currentData = this.extractEventDataFromRoute(activatedRoute);

    let data = this.buildInitialEventData();
    data = this.applyStepData(data, 3, 'Signup mobile page', 'started');
    this.redCrossService.track('Signup Step', data, false);
  }

  MobilePageCompleted(activatedRoute: ActivatedRouteSnapshot, ctaText: string) {
    this.currentData = this.extractEventDataFromRoute(activatedRoute);
    this.currentData.ctaText = ctaText;

    let data = this.buildInitialEventData();
    data = this.applyStepData(data, 3, 'Signup mobile page', 'completed');
    this.redCrossService.track('Signup Step', data, false);
  }

  EmailTaken() {
    let data = this.buildInitialEventData();
    data = this.applyStepData(data, 1, 'Signup email taken', 'in-progress');
    this.platformAnalyticsDataService.clearCache();
    this.redCrossService.track('Signup Step', data, false);
  }

  async StepAppCompleted() {
    let data = this.buildInitialEventData();
    data = this.applyStepData(data, 1, 'Signup App', 'completed');
    this.platformAnalyticsDataService.clearCache();
    await this.redCrossService.trackAsync('Signup Step', data, true).pipe(first()).toPromise();
    await this.redCrossService.trackAsync('Signup', data, true).pipe(first()).toPromise();
  }

  // Private Methods

  private extractEventDataFromRoute(activatedRoute: ActivatedRouteSnapshot) {
    let routeData: SignupEventData = {};

    if (activatedRoute) {
      routeData.signupUTMCampaign = activatedRoute.queryParams['utm_campaign'];
      routeData.signupUTMSource = activatedRoute.queryParams['utm_source'];
      routeData.signupUTMMedium = activatedRoute.queryParams['utm_medium'];
      routeData.product = activatedRoute.queryParams['product'];
      routeData.productIntent = activatedRoute.queryParams['product'];
      routeData.appKey = activatedRoute.queryParams['appkey'];
      routeData.website = activatedRoute.queryParams['url'];
      routeData.storeDomain = activatedRoute.queryParams['shop_domain'];
      routeData.email = activatedRoute.queryParams['email'];

      let platform_id = activatedRoute.queryParams['platform_id'];
      let platform = activatedRoute.queryParams['platform'];
      routeData.platform = getPlatformByKey('name', platform) || getPlatformByKey('id', platform_id);
    }

    return routeData;
  }

  private buildInitialEventData() {
    let data: SignupEventData = {
      url: window.location.href,
      referrer: document.referrer
    };

    if (this.currentData) {
      data = _.assign(data, this.currentData);
    }

    return data;
  }

  private applyStepData(data: SignupEventData, step: number, name: string, status: string) {
    data.phase = step;
    data.stepNum = step;
    data.phaseName = name || `Signup Step ${step}`;
    data.stepName = name || `Signup Step ${step}`;
    data.status = status;
    return data;
  }
}
